import { FC, MouseEvent, useRef, useState } from "react";
import styled from "styled-components";
import { AnimatePresence } from "framer-motion";
import { useParams } from "react-router-dom";

import { Modal } from "@/components/Modal";
import Text from "@/components/Text";
import { CloseButton, StyledDialog } from "@/components/AlertDialogs/Dialog";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import UploadForm from "./UploadForm";
import UploadTypeSelection from "./UploadTypeSelection";

const StyledText = styled(Text)`
  margin-top: 8px;
`;

const Headings = styled.div`
  margin-bottom: 16px;
`;

const Dialog = styled(StyledDialog)`
  padding-bottom: 24px;
  height: 348px;
  display: flex;
  flex-direction: column;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  color: ${({ theme }) => theme.colors.black};
  position: absolute;
  top: 24px;
  left: 24px;
  transition: 0.2s ease-out border-color;
  &:hover {
    border-color: ${({ theme }) => theme.colors.black};
  }
`;

export interface ImportType {
  title: string;
  url: string;
  postBody?: {
    [key: string]: any;
  };
}

interface Props {
  show: boolean;
  onClose: () => void;
}

const UploadModal: FC<Props> = ({ onClose: closeModal, show }) => {
  const { teamId } = useParams();

  const importTypes: ImportType[] = [
    {
      title: "Videos or Topics data",
      url: "/v1/csv-import",
    },
    {
      title: "Bored Panda channels",
      url: `/v1/csv-import/channels`,
      postBody: {
        team_id: teamId,
        is_competitor: 0,
      },
    },
    {
      title: "Competitor channels",
      url: "/v1/csv-import/channels",
      postBody: {
        team_id: teamId,
        is_competitor: 1,
      },
    },
  ];

  const [importType, setImportType] = useState<ImportType | null>(null);

  const ref = useRef<HTMLDivElement>(null);

  const onClose = () => {
    closeModal();
    setImportType(null);
  };

  useOnClickOutside(ref, onClose);

  const onCloseWithEventPrevention = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  const renderActiveScreen = () => {
    if (importType) {
      return <UploadForm onClose={onClose} activeImportType={importType} />;
    }

    return (
      <UploadTypeSelection
        importTypes={importTypes}
        setImportType={setImportType}
      />
    );
  };

  const onBack = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setImportType(null);
  };

  return (
    <AnimatePresence>
      {show && (
        <Modal zIndex={50}>
          <Dialog
            ref={ref}
            initial={{ scale: 0.3 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.3 }}
          >
            {!!importType && (
              <BackButton onClick={onBack}>
                <svg
                  width="12"
                  height="10"
                  viewBox="0 0 12 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M4.15623 0.805768C4.53964 0.408261 5.1727 0.396836 5.57021 0.78025C5.96772 1.16366 5.97914 1.79673 5.59573 2.19423L3.85578 3.99813H10.5C11.0523 3.99813 11.5 4.44585 11.5 4.99813C11.5 5.55042 11.0523 5.99813 10.5 5.99813H3.85492L5.59573 7.80292C5.97914 8.20043 5.96772 8.83349 5.57021 9.21691C5.1727 9.60032 4.53964 9.58889 4.15623 9.19139L0.781687 5.69281C0.703472 5.61172 0.641629 5.52072 0.596158 5.42395C0.535269 5.29476 0.501228 5.15042 0.501228 4.99813C0.501228 4.71756 0.616779 4.46397 0.802889 4.28236L4.15623 0.805768Z"
                    fill="currentColor"
                  />
                </svg>
              </BackButton>
            )}
            <CloseButton onClick={onCloseWithEventPrevention}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13 1L1 13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13 13L1 1"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </CloseButton>
            <Headings>
              {!!!importType ? (
                <Text $type="heading2">Choose type:</Text>
              ) : (
                <>
                  <Text $type="heading2">Import file</Text>
                  <StyledText $type="smallMedium">
                    {importType.title}
                  </StyledText>
                </>
              )}
            </Headings>
            {renderActiveScreen()}
          </Dialog>
        </Modal>
      )}
    </AnimatePresence>
  );
};

export default UploadModal;
